import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, type UseInfiniteQueryOptions, type UseMutationOptions, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { fetchAddHeartMission, fetchAddMission, fetchBjMissionList, fetchMissionSponList, fetchMyMission, fetchPlayMissionList } from '../../apis/client-side/mission';
import type { AddHeartMission, FetchAddMissionParams, FetchBjMissionListParams, FetchBjMissionListResponse, FetchMissionSponListParams, FetchMissionSponListResponse, FetchMyMissionListParams, FetchMyMissionListResponse, FetchPlayMissionListParams, FetchPlayMissionListResponse } from '../../apis/client-side/mission/type';
import type { ApiBaseResponse } from '../../apis/type';
import TimeConstants from '../../utils/item';
import type { CustomUseQueryOptions } from '../type';
import { missionListQueryKeys } from './missionQuerykeys';
export const useFetchMyMissionListQuery = (FetchMyMissionListParams, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== FetchMyMissionListParams) {
    t0 = missionListQueryKeys.fetchMyMissionList(FetchMyMissionListParams);
    $[0] = FetchMyMissionListParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== FetchMyMissionListParams) {
    t1 = () => fetchMyMission(FetchMyMissionListParams);
    $[2] = FetchMyMissionListParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      staleTime: TimeConstants.HALF_SECOND * 10,
      placeholderData: keepPreviousData
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useFetchMyMissionListInfiniteQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = missionListQueryKeys.fetchMyMissionList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      const actualPageParam = typeof pageParam === "number" ? pageParam : 1;
      const offset = params.limit * ((actualPageParam || 1) - 1);
      return fetchMyMission({
        ...params,
        offset
      });
    };
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      initialPageParam: 1,
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      getNextPageParam: _temp2,
      select: _temp4
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
};
export const useFetchBjMissionListQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = missionListQueryKeys.fetchBjMissionList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = () => fetchBjMissionList(params);
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      staleTime: TimeConstants.HALF_SECOND * 10,
      placeholderData: keepPreviousData
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useFetchBjMissionListInfiniteQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = missionListQueryKeys.fetchBjMissionList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      const actualPageParam = typeof pageParam === "number" ? pageParam : 1;
      const offset = (params.limit ?? 10) * ((actualPageParam || 1) - 1);
      return fetchBjMissionList({
        ...params,
        offset
      });
    };
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      initialPageParam: 1,
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      getNextPageParam: _temp6,
      select: _temp8
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
};
export const useFetchPlayMissionListQuery = (FetchPlayGetMissionListParams, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== FetchPlayGetMissionListParams) {
    t0 = missionListQueryKeys.fetchPlayMissionList(FetchPlayGetMissionListParams || "play-get-mission");
    $[0] = FetchPlayGetMissionListParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== FetchPlayGetMissionListParams) {
    t1 = () => fetchPlayMissionList(FetchPlayGetMissionListParams);
    $[2] = FetchPlayGetMissionListParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      staleTime: TimeConstants.HALF_SECOND * 10,
      placeholderData: keepPreviousData
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useFetchMissionSponListQuery = (params, options) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== params) {
    t0 = missionListQueryKeys.fetchMissionSponList(params);
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params) {
    t1 = () => fetchMissionSponList(params);
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0.queryKey || $[6] !== t1) {
    t2 = {
      queryKey: t0.queryKey,
      queryFn: t1,
      ...options,
      staleTime: TimeConstants.HALF_SECOND * 10,
      placeholderData: keepPreviousData
    };
    $[4] = options;
    $[5] = t0.queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const usePostMissionMutation = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp9,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useAddMissionHeartMutation = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp10,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
function _temp(page) {
  return page.list;
}
function _temp2(lastPage, allPages) {
  const totalFetchedItems = allPages.flatMap(_temp).length;
  if (totalFetchedItems < lastPage.page.total) {
    const nextPage = allPages.length + 1;
    return nextPage;
  }
}
function _temp3(page_0) {
  return page_0.list ?? [];
}
function _temp4(data) {
  return {
    pages: data?.pages.flatMap(_temp3),
    pageParams: data.pageParams,
    total: data?.pages.length > 0 ? data?.pages[0].page.total : 0
  };
}
function _temp5(page) {
  return page.list;
}
function _temp6(lastPage, allPages) {
  const totalFetchedItems = allPages.flatMap(_temp5).length;
  if (totalFetchedItems < lastPage.page.total) {
    const nextPage = allPages.length + 1;
    return nextPage;
  }
}
function _temp7(page_0) {
  return page_0.list ?? [];
}
function _temp8(data) {
  return {
    pages: data?.pages.flatMap(_temp7),
    pageParams: data.pageParams,
    total: data?.pages.length > 0 ? data?.pages[0].page.total : 0
  };
}
function _temp9(params) {
  return fetchAddMission(params);
}
function _temp10(params) {
  return fetchAddHeartMission(params);
}